import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api, useCart } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ProductCard from "@/components/ProductCard";
import {
  ShoppingCart,
  GitCompare,
  Share2,
  ChevronLeft,
  Plus,
  Minus,
  MessageCircle,
  Truck,
  Shield,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { productId } = useParams();
  const { addToCart, addToCompare } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/products/${productId}`);
      setProduct(response.data);
      
      // Fetch related products from same category
      if (response.data.category_id) {
        const relatedRes = await api.get(`/products?category=${response.data.category_id}&limit=4`);
        setRelatedProducts(relatedRes.data.products.filter(p => p.product_id !== productId));
      }
    } catch (error) {
      console.error("Error fetching product:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product.product_id, quantity);
    setQuantity(1);
  };

  const handleWhatsAppInquiry = () => {
    const message = `Hola! Estoy interesado en el producto: *${product.name}*\nPrecio: USD $${product.price}\nURL: ${window.location.href}`;
    const whatsappUrl = `https://wa.me/59899179773?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: `Mirá este producto en Barraca Maldonado: ${product.name}`,
          url: window.location.href,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Enlace copiado al portapapeles");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-UY", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-lg text-muted-foreground mb-4">Producto no encontrado</p>
        <Link to="/tienda">
          <Button>Volver a la tienda</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="bg-muted py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-muted-foreground hover:text-foreground">
              Inicio
            </Link>
            <span className="text-muted-foreground">/</span>
            <Link to="/tienda" className="text-muted-foreground hover:text-foreground">
              Tienda
            </Link>
            <span className="text-muted-foreground">/</span>
            <span className="text-foreground truncate">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button - Mobile */}
        <Link to="/tienda" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 md:hidden">
          <ChevronLeft className="w-4 h-4" />
          Volver
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Images */}
          <div className="space-y-4">
            {/* Main image */}
            <div className="aspect-square bg-white rounded-sm border overflow-hidden">
              {product.images?.length > 0 ? (
                <img
                  src={product.images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-contain p-4"
                  data-testid="product-main-image"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  Sin imagen
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {product.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-sm border overflow-hidden ${
                      selectedImage === index ? "border-primary border-2" : ""
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product info */}
          <div className="space-y-6">
            {/* Brand & SKU */}
            <div className="flex items-center gap-4">
              {product.brand && (
                <span className="text-sm text-muted-foreground uppercase tracking-wider">
                  {product.brand}
                </span>
              )}
              {product.sku && (
                <span className="text-sm text-muted-foreground">
                  SKU: {product.sku}
                </span>
              )}
            </div>

            {/* Name */}
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl" data-testid="product-name">
              {product.name}
            </h1>

            {/* Price */}
            <div className="flex items-baseline gap-3">
              <span className="font-heading text-4xl text-accent" data-testid="product-price">
                {formatPrice(product.price)}
              </span>
              <span className="text-sm text-muted-foreground">IVA incluido</span>
            </div>

            {/* Stock */}
            <p className={`text-sm font-medium ${product.stock > 0 ? "text-green-600" : "text-destructive"}`}>
              {product.stock > 0 ? `${product.stock} unidades disponibles` : "Sin stock"}
            </p>

            {/* Quantity & Add to cart */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center border rounded-sm">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="rounded-none"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Input
                  type="number"
                  min="1"
                  max={product.stock}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock, parseInt(e.target.value) || 1)))}
                  className="w-16 text-center border-0 rounded-none"
                  data-testid="quantity-input"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  className="rounded-none"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              <Button
                onClick={handleAddToCart}
                disabled={product.stock <= 0}
                className="flex-1 bg-accent hover:bg-accent/90 text-white font-bold uppercase tracking-wide h-12"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Agregar al Carrito
              </Button>
            </div>

            {/* Secondary actions */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={handleWhatsAppInquiry}
                className="flex-1 sm:flex-none gap-2 text-[#25D366] border-[#25D366] hover:bg-[#25D366]/10"
                data-testid="whatsapp-inquiry-btn"
              >
                <MessageCircle className="w-4 h-4" />
                Consultar
              </Button>
              <Button
                variant="outline"
                onClick={() => addToCompare(product)}
                className="gap-2"
              >
                <GitCompare className="w-4 h-4" />
                Comparar
              </Button>
              <Button variant="outline" onClick={handleShare} className="gap-2">
                <Share2 className="w-4 h-4" />
                Compartir
              </Button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t">
              <div className="flex items-center gap-3">
                <Truck className="w-6 h-6 text-primary" />
                <div>
                  <p className="text-sm font-medium">Envío Gratis</p>
                  <p className="text-xs text-muted-foreground">En Maldonado</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-primary" />
                <div>
                  <p className="text-sm font-medium">Garantía</p>
                  <p className="text-xs text-muted-foreground">Oficial</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <RotateCcw className="w-6 h-6 text-primary" />
                <div>
                  <p className="text-sm font-medium">Devoluciones</p>
                  <p className="text-xs text-muted-foreground">30 días</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs: Description & Specs */}
        <Tabs defaultValue="description" className="mt-12">
          <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-auto p-0">
            <TabsTrigger
              value="description"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent font-heading uppercase tracking-wide"
            >
              Descripción
            </TabsTrigger>
            <TabsTrigger
              value="specs"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent font-heading uppercase tracking-wide"
            >
              Especificaciones
            </TabsTrigger>
          </TabsList>
          <TabsContent value="description" className="pt-6">
            <div className="prose max-w-none">
              {product.description ? (
                <p>{product.description}</p>
              ) : (
                <p className="text-muted-foreground">Sin descripción disponible.</p>
              )}
            </div>
          </TabsContent>
          <TabsContent value="specs" className="pt-6">
            {product.specs && Object.keys(product.specs).length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.entries(product.specs).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">{key}</span>
                    <span className="font-medium">{value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">Sin especificaciones disponibles.</p>
            )}
          </TabsContent>
        </Tabs>

        {/* Related products */}
        {relatedProducts.length > 0 && (
          <section className="mt-16">
            <h2 className="font-heading text-2xl mb-6">PRODUCTOS RELACIONADOS</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {relatedProducts.slice(0, 4).map((prod) => (
                <ProductCard key={prod.product_id} product={prod} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
